import { Outlet, useLocation } from '@remix-run/react'
import { useEffect, useRef } from 'react'
import { type MapRef } from 'react-map-gl'
import {
  aCheckedListings,
  aMapCurrentCount,
  aMapRef,
  aOrganization,
  aSelectedListing,
  aUser,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  CheckAllListings,
  MapBuildingsLayer,
  MapCardList,
  MapDataLayers,
  MapDrawControl,
  MapFilterSheet,
  MapGeographiesLayer,
  MapLayersSheet,
  MapLegend,
  MapListingCountLoading,
  MapNavigationControl,
  MapPropertyClustersLayer,
  MapReportActions,
  MapSavedSearches,
  MapSearch,
  MapViewControls,
  useMapListings,
  useMapProperties,
  type MapOutletContextData,
} from '~/components'
import MapParcelsLayer from '~/components/app/map/layers/Parcels'
import {
  MapView,
  useCurrentMapView,
  useResetMapState,
  useRouteMeta,
} from '~/hooks'
import { cn } from '~/utils'
import ListingsMap from './Map'

export default function Container() {
  const mapRef = useRef<MapRef>(null)
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const setMapRef = useSetAtom(aMapRef)
  const currentMapView = useCurrentMapView()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const checkedListings = useAtomValue(aCheckedListings)
  const location = useLocation()
  const { setRouteMeta } = useRouteMeta()
  useResetMapState()

  useEffect(() => {
    setRouteMeta({
      renderedMainMap: true,
    })
  }, [location.key])

  useEffect(() => {
    if (mapRef.current) {
      setMapRef(mapRef.current!)
    }
  }, [mapRef.current, setMapRef])

  useEffect(() => {
    return () => setSelectedListing(null)
  }, [])

  const { properties, totalCount, isLoadingProperties, isFetchingProperties } =
    useMapProperties()

  const {
    listings,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useMapListings({})

  return (
    <ListingsMap mapRef={mapRef}>
      <div className="absolute left-2 top-2 z-50 flex gap-2 md:left-4 md:top-4 lg:left-16">
        <MapNavigationControl />
        {organization!.options.listingsEnabled && (
          <div className="flex gap-2">
            <MapReportActions
              listings={listings}
              currentMapCount={currentMapCount}
            />
            <MapDrawControl className="hidden sm:flex" />
            {checkedListings.length > 0 && (
              <CheckAllListings
                listings={listings}
                currentMapCount={currentMapCount}
              />
            )}
          </div>
        )}
      </div>

      {organization!.options.listingsEnabled && (
        <MapListingCountLoading
          className="absolute bottom-2 left-1/2 z-50 -translate-x-1/2 md:bottom-4 lg:bottom-auto lg:top-4"
          isLoading={isLoadingProperties || isFetchingProperties}
          count={currentMapCount}
          totalCount={totalCount}
        />
      )}

      <div
        className={cn(
          'absolute right-14 top-2 flex gap-2 sm:flex-row md:right-16 md:top-4',
          user ? 'flex-col-reverse' : 'flex-row'
        )}>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          {organization!.options.listingsEnabled && (
            <>
              <MapSearch />
              <MapSavedSearches mapRef={mapRef} />
            </>
          )}
        </div>
        <MapLayersSheet />
        {organization!.options.listingsEnabled && <MapFilterSheet />}
      </div>
      <MapViewControls className="absolute right-2 top-2 flex flex-col gap-2 md:right-4 md:top-4" />
      {organization!.options.listingsEnabled && (
        <MapLegend className="hidden sm:block" />
      )}
      <MapGeographiesLayer />
      <MapBuildingsLayer />
      {organization!.options.dataLayersEnabled && <MapDataLayers />}
      {organization!.options.parcelDataEnabled && <MapParcelsLayer />}
      {organization!.options.listingsEnabled && (
        <MapPropertyClustersLayer properties={properties} />
      )}

      {organization!.options.listingsEnabled ? (
        <div
          className={cn(
            currentMapView === MapView.Map &&
              'absolute bottom-0 left-4 top-18 flex'
          )}>
          {currentMapView === MapView.Map && (
            <MapCardList
              listings={listings}
              isLoading={isLoading}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            />
          )}

          <Outlet
            context={
              {
                listings,
                isError,
                isLoading,
                isFetching,
                fetchNextPage,
                hasNextPage,
              } as MapOutletContextData
            }
          />
        </div>
      ) : (
        <Outlet />
      )}
    </ListingsMap>
  )
}
